<template>
  <section id="credential-management">
    <ListCredential
      ref="listCredential"
      :items="listCredential"
      :loading="loading"
      :is-admin="isAdmin"
      @tambah="$refs.modalTambah.show()"
      @edit="handleEdit"
      @delete="handleDelete"
      @assign="handleAssign"
      @pin-expired="handlePinExpired"
      @search="handleSearch"
      @load-more="handleLoadMore"
      @copy-password-request="handleCopyPasswordRequest"
      @copy-success="handleCopySuccess"
      @copy-error="handleCopyError"
    />

    <ModalPin
      ref="modalPin"
      @verified="handlePinVerified"
      @cancel="handlePinBatal"
    />

    <AddCredential
      ref="modalTambah"
      @submitted="handleSubmitted"
      @submit-error="handleSubmitError"
      @pin-expired-check="handlePinExpiredCheck"
      @pin-session-expired="handlePinSessionExpired"
    />

    <EditCredential
      ref="modalEdit"
      @updated="handleUpdated"
      @update-error="handleUpdateError"
      @pin-expired-check="handlePinExpiredCheck"
      @pin-session-expired="handlePinSessionExpired"
    />

    <DeleteCredential
      ref="modalDelete"
      @deleted="handleDeleted"
      @delete-error="handleDeleteError"
      @pin-session-expired="handlePinSessionExpired"
    />
    <AssignCredential
      ref="modalAssign"
      @assigned="handleAssigned"
      @assign-error="handleAssignError"
      @pin-session-expired="handlePinSessionExpired"
    />
  </section>
</template>

<script>
import { alertError, alertSuccess } from '@toast'
import { credentialAxios } from './components/config'
import ListCredential from './components/ListCredential.vue'
import ModalPin from './components/ModalPin.vue'
import AddCredential from './components/Add.vue'
import EditCredential from './components/Edit.vue'
import DeleteCredential from './components/Delete.vue'
import AssignCredential from './components/Assign.vue'

export default {
  components: {
    ListCredential,
    ModalPin,
    AddCredential,
    EditCredential,
    DeleteCredential,
    AssignCredential,
  },

  data() {
    return {
      loading: false,
      listCredential: [],
      offset: 0,
      limit: 15,
      lastData: false,
      isPinVerified: false,
      pendingCallback: null,
      alertError,
      alertSuccess,
      previousRoute: null,
      keyword: '',
      isAdmin: false,
    }
  },

  created() {
    const role = this.$store.state.auth.userData.role_name
    if (role && role.toUpperCase() === 'ADMIN') {
      this.isAdmin = true
    }
  },

  beforeRouteEnter(to, from, next) {
    next(vm => {
      // eslint-disable-next-line no-param-reassign
      vm.previousRoute = from
    })
  },

  mounted() {
    this.$nextTick(() => {
      this.$refs.modalPin.show()
    })
  },

  methods: {

    handlePinExpired(payload) {
      this.isPinVerified = false
      this.pendingCallback = payload?.onValid || null
      this.$refs.modalPin.show()
    },

    handlePinExpiredCheck({ onValid }) {
      if (this.isPinVerified) {
        onValid()
      } else {
        this.pendingCallback = onValid
        this.$refs.modalPin.show()
      }
    },

    handlePinSessionExpired({ onValid }) {
      this.isPinVerified = false
      this.pendingCallback = onValid
      this.$bvModal.hide('modal-tambah-credential')
      this.$bvModal.hide('modal-edit-credential')
      this.$bvModal.hide('modal-delete-credential')
      this.$bvModal.hide('modal-assign-credential')
      this.$refs.modalPin.show()
    },

    async handleSearch(val) {
      this.keyword = val
      this.offset = 0
      this.lastData = false
      this.listCredential = []
      await this.getDataCredential()
    },

    handlePinBatal() {
      if (this.previousRoute && this.previousRoute.name) {
        this.$router.push({ name: this.previousRoute.name })
      } else {
        this.$router.push('/')
      }
    },

    handleCopyPasswordRequest(item) {
      if (this.isPinVerified) {
        this.$refs.listCredential.doCopyPassword(item)
      } else {
        this.pendingCallback = () => this.$refs.listCredential.doCopyPassword(item)
        this.$refs.modalPin.show()
      }
    },

    mapCredential(item) {
      return {
        id: item.id,
        platform: item.platform_name,
        loginUrl: item.platform_url,
        email: item.identifier,
        password: null,
        note: item.notes || '-',
        category: item.category,
      }
    },

    async handlePinVerified(pin) {
      try {
        const res = await credentialAxios.post('v1/credential_management/verify_pin', { pin })

        this.isPinVerified = true
        // Store PIN token and its expiry (5 minutes) for request interceptor
        sessionStorage.setItem('pin_token', pin)
        const expiry = Date.now() + 5 * 60 * 1000
        sessionStorage.setItem('pin_expiry', expiry.toString())
        this.$refs.modalPin.hide()

        if (this.pendingCallback) {
          const cb = this.pendingCallback
          this.pendingCallback = null
          cb()
        } else {
          await this.getDataCredential()
        }
      } catch (err) {
        if (err.response?.status === 403) {
          this.$refs.modalPin.setError('PIN salah, coba lagi.')
        } else {
          this.$refs.modalPin.setError('Terjadi kesalahan, coba lagi.')
        }
      }
    },

    async getDataCredential(isLoadMore = false) {
      if (!this.isPinVerified) return

      if (this.loading || this.lastData) return

      this.loading = true
      try {
        const url = this.isAdmin
          ? 'v1/credential_management/secure'
          : 'v1/credential_management/secure/staff'

        const res = await credentialAxios.get(
          `${url}?offset=${this.offset}&limit=${this.limit}&keyword=${this.keyword}`,
        )
        const { data } = res.data

        const mapped = data.map(item => this.mapCredential(item))

        if (isLoadMore) {
          this.listCredential = [
            ...this.listCredential,
            ...mapped,
          ]
          this.offset += data.length
        } else {
          this.listCredential = mapped
          this.offset = data.length
        }
        if (data.length < this.limit) {
          this.lastData = true
        }
      } catch (err) {
        if (err.response?.status === 403) {
          this.isPinVerified = false
          this.pendingCallback = () => this.getDataCredential()
          this.$refs.modalPin.show()
        } else {
          this.alertError(err)
        }
      } finally {
        this.loading = false
      }
    },

    async handleLoadMore() {
      if (this.loading || this.lastData) return

      await this.getDataCredential(true)
    },
    // Add
    async handleSubmitted() {
      this.offset = 0
      this.lastData = false
      this.listCredential = []

      await this.getDataCredential()
      this.alertSuccess('Berhasil menambahkan data')
    },
    handleSubmitError(err) {
      this.alertError(err)
    },
    // Edit
    handleEdit(item) {
      this.$refs.modalEdit.show(item)
    },
    async handleUpdated() {
      this.offset = 0
      this.lastData = false
      this.listCredential = []

      await this.getDataCredential()
      this.alertSuccess('Data berhasil diperbaharui')
    },
    handleUpdateError(err) {
      this.alertError(err)
    },
    // Delete
    handleDelete(item) {
      this.$refs.modalDelete.show(item)
    },
    async handleDeleted() {
      this.offset = 0
      this.lastData = false
      this.listCredential = []

      await this.getDataCredential()
      this.alertSuccess('Kredensial berhasil dihapus')
    },
    handleDeleteError(err) {
      this.alertError(err)
    },

    // ASSIGN
    handleAssign(item) {
      this.$refs.modalAssign.show(item)
    },
    handleAssigned({ hasRevoke }) {
      if (hasRevoke) {
        this.alertSuccess('Berhasil menghapus akses')
      } else {
        this.alertSuccess('Berhasil membagikan akses')
      }
    },
    handleAssignError(err) {
      this.alertError(err)
    },

    // Copy
    handleCopy(item) {
      this.$refs.listCredential.doCopyPassword(item)
    },
    handleCopySuccess() {
      this.alertSuccess('Password berhasil disalin')
    },
    handleCopyError(err) {
      this.alertError(err)
    },
  },
}
</script>
